/**
 * Sync contract (see docs/ARCHITECTURE.md §5–6).
 *
 *  - every synced row has a client-generated UUIDv7 `id` and a `space_id`
 *  - every local write is stamped with an HLC and recorded in an outbox in
 *    the same transaction as the row itself
 *  - deletes are tombstones (`deleted_at`), never physical deletes
 *  - conflicts resolve per field, last-writer-wins by HLC, on the server
 *
 * The transport is the only part that knows about the network; the engine
 * (engine.ts) and the tests run the same code against a real Postgres.
 */
export type SyncedTable =
  | 'budget_spaces'
  | 'budget_members'
  | 'categories'
  | 'subcategories'
  | 'recurring_templates'
  | 'transactions'
  | 'consumption_data'
  | 'debt_details'
  | 'activity_entries';

/**
 * Parents before children: the order remote rows are applied in, so a
 * transaction never arrives before the category it points at.
 */
export const SYNCED_TABLES: readonly SyncedTable[] = [
  'budget_spaces',
  'budget_members',
  'categories',
  'subcategories',
  'recurring_templates',
  'transactions',
  'consumption_data',
  'debt_details',
  'activity_entries',
];

/** One local write on its way up. */
export interface Change {
  table: SyncedTable;
  id: string;
  spaceId: string;
  /** Only the fields that changed. */
  fields: Record<string, unknown>;
  hlc: string;
  /** The values these fields had on this device before the write (absent for a new row). */
  prev?: Record<string, unknown>;
}

export interface PushResult {
  /** HLCs the server accepted. */
  accepted: string[];
  /** Changes it refused (e.g. a budget this account cannot write). */
  rejected: { hlc: string; reason: string }[];
  /**
   * Changes it did not take yet: the budget's owner has no Premium. The
   * device keeps them and sends them again (absent from servers before Premium).
   */
  deferred?: string[];
}

/** This account's Premium, as the server knows it (the RevenueCat webhook writes it). */
export interface AccountPremium {
  active: boolean;
  expiresAt: number | null;
  willRenew: boolean | null;
}

/** A row as the server holds it: every field, merged, with the HLC that set each one. */
export interface RemoteRecord {
  table: SyncedTable;
  id: string;
  spaceId: string;
  fields: Record<string, unknown>;
  fieldHlc: Record<string, string>;
  seq: number;
}

export interface PullResult {
  records: RemoteRecord[];
  /** Pass back on the next pull. */
  cursor: number;
  hasMore: boolean;
}

/** A one-time invitation to a budget (7 days). */
export interface Invite {
  code: string;
  spaceId: string;
  /** ISO timestamp. */
  expiresAt: string;
}

export type AcceptResult =
  | { ok: true; spaceId: string; role: 'owner' | 'member' }
  /** no_premium: the budget's owner has no Premium (any more), so it takes no new members. */
  | { ok: false; reason: 'invalid' | 'used' | 'expired' | 'too_many' | 'no_premium' };

export interface SyncTransport {
  /** Link the signed-in account to an app user id; returns the id this person uses everywhere. */
  claim(localUserId: string): Promise<string>;
  push(changes: readonly Change[]): Promise<PushResult>;
  /** Changes after `cursor`; with `spaceId`, only that budget's (a budget just joined). */
  pull(cursor: number, limit?: number, spaceId?: string): Promise<PullResult>;

  /* Sharing (Phase 18): the server decides who may do what. */
  /** Budgets this account can see, and its role in each. */
  access(): Promise<{ spaceId: string; role: 'owner' | 'member'; premium: boolean }[]>;
  createInvite(spaceId: string): Promise<Invite>;
  listInvites(spaceId: string): Promise<Invite[]>;
  revokeInvite(code: string): Promise<void>;
  acceptInvite(code: string): Promise<AcceptResult>;
  removeMember(spaceId: string, userId: string): Promise<void>;
  leaveSpace(spaceId: string): Promise<void>;
  /** This account's Premium (bought on any device). */
  premium(): Promise<AccountPremium>;
  /** Delete this account and its budgets nobody else uses (shared ones go on for the others). */
  deleteAccount(): Promise<void>;
}
